import { DateTime } from 'luxon';

/**
 * Buckets a notification into the panel's date sections.
 *
 * Boundaries are calendar days, not a rolling 24h window: a notification from
 * yesterday at 23:00 belongs to `week` at 01:00 today, not to `today`.
 */
export const NotificationDateGroup = {
  TODAY: 'today',
  WEEK: 'week',
  OLDER: 'older',
} as const;

export type NotificationDateGroup =
  (typeof NotificationDateGroup)[keyof typeof NotificationDateGroup];

export const NOTIFICATION_DATE_GROUP_ORDER: NotificationDateGroup[] = [
  NotificationDateGroup.TODAY,
  NotificationDateGroup.WEEK,
  NotificationDateGroup.OLDER,
];

/**
 * Resolves the two day boundaries once, then classifies each notification with
 * a pair of integer comparisons. Luxon still owns the boundary maths — which is
 * where the zone and DST subtleties live — but it is kept out of the per-item
 * path, which runs over the whole list again on every page fetch.
 */
export function createNotificationDateGrouper(now: DateTime = DateTime.now()) {
  const startOfToday = now.startOf('day');
  const todayMs = startOfToday.toMillis();
  // `week` covers the six calendar days before today; day seven is `older`.
  const weekMs = startOfToday.minus({ days: 6 }).toMillis();

  return function resolveGroup(isoString: string | null | undefined): NotificationDateGroup {
    if (!isoString) return NotificationDateGroup.OLDER;

    const occurredAtMs = Date.parse(isoString);
    if (Number.isNaN(occurredAtMs)) return NotificationDateGroup.OLDER;

    // Clock skew can put a fresh notification slightly in the future.
    if (occurredAtMs >= todayMs) return NotificationDateGroup.TODAY;
    if (occurredAtMs >= weekMs) return NotificationDateGroup.WEEK;

    return NotificationDateGroup.OLDER;
  };
}
